import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCsrfToken } from '../../utils/csrf';
import { FormData } from '../models/ModelIncome';

export const useIncomeImport = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    dateType: '',
    month: '',
    year: '',
    regime: '',
    grossSalary: '',
    vr: '',
    otherIncome: '',
    otherIncomeValue: '',
    otherIncomeTaxed: '',
    taxRate: '',
  });
  const navigate = useNavigate();
  const totalSteps = 9;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleOptionSelect = (name: string, value: string) => {
    setFormData(prevData => ({ ...prevData, [name]: value }));

    if (name === 'otherIncome' && value === 'no') {
      setFormData(prevData => ({
        ...prevData,
        otherIncomeValue: '0',
        otherIncomeTaxed: 'no',
        taxRate: '0',
      }));
      navigateToStep(9);
    } else if (name === 'otherIncomeTaxed' && value === 'no') {
      setFormData(prevData => ({ ...prevData, taxRate: '0' }));
      navigateToStep(9);
    } else if (name === 'regime') {
      navigateToStep(3);
    } else {
      navigateToStep(currentStep + 1);
    }
  };

  const navigateToStep = (step: number) => {
    setCurrentStep(step);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only validate and submit if we're on the final step and user confirmed
    if (currentStep === totalSteps && isConfirmed) {
      const requiredFields = ['regime', 'grossSalary', 'vr'];
      if (formData.otherIncome === 'yes') {
        requiredFields.push('otherIncomeValue', 'otherIncomeTaxed');
        if (formData.otherIncomeTaxed === 'yes') {
          requiredFields.push('taxRate');
        }
      }

      const missingFields = requiredFields.filter(field => !formData[field as keyof FormData]);
      if (missingFields.length > 0) {
        alert(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      // If dateType is 'previous', check for month and year
      if (formData.dateType === 'previous' && (!formData.month || !formData.year)) {
        alert('Please select month and year');
        return;
      }

      try {
        const csrfToken = await getCsrfToken();

        // Format date string properly
        let dateString;
        if (formData.dateType === 'current') {
          const now = new Date();
          dateString = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        } else {
          // Ensure month and year are properly formatted
          if (!formData.month || !formData.year) {
            throw new Error('Month and year are required');
          }
          dateString = `${formData.year}-${formData.month.padStart(2, '0')}-01`;
        }

        const response = await fetch('/api/income/import/', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          body: JSON.stringify({
            date: dateString,
            regime: formData.regime.toLowerCase(),
            gross_salary: formData.grossSalary,
            vr: formData.vr,
            other_income: formData.otherIncome,
            other_income_value: formData.otherIncomeValue,
            other_income_taxed: formData.otherIncomeTaxed,
            tax_rate: formData.taxRate
          }),
        });
    
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || 'Failed to import income');
          }
          navigate('/income');
        } else {
          const text = await response.text();
          console.error('Received non-JSON response:', text);
          throw new Error('Received non-JSON response from server');
        }
      } catch (error) {
        console.error('Error importing income:', error);
        alert(`Error importing income: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
      }
    } else if (currentStep === totalSteps) {
      // If on final step but not confirmed, set confirmed to true
      setIsConfirmed(true);
    }
  };

  const handleDateSelect = (type: 'current' | 'previous') => {
    if (type === 'current') {
      const currentDate = new Date();
      setFormData(prevData => ({ 
        ...prevData,
        dateType: type,
        month: String(currentDate.getMonth() + 1).padStart(2, '0'),
        year: String(currentDate.getFullYear())
      }));
      navigateToStep(2);
    } else {
      setFormData(prevData => ({ ...prevData, dateType: type }));
    }
  };

  const handleMonthChange = (month: string) => {
    setFormData(prevData => ({ ...prevData, month }));
  };

  const handleYearChange = (year: string) => {
    setFormData(prevData => ({ ...prevData, year }));
  };

  return {
    currentStep,
    formData,
    totalSteps,
    handleInputChange,
    handleOptionSelect,
    handleDateSelect,
    handleMonthChange,
    handleYearChange,
    navigateToStep,
    handleSubmit,
    isConfirmed,
    setIsConfirmed
  };
};